// src/components/common/ProtectedRoute.js
import React from 'react';
import { hasPermission, ROLE_HIERARCHY, getRoleDisplayName } from '../../utils/roles';
import { Shield, Lock } from 'lucide-react';

/**
 * ProtectedRoute wraps content that requires authentication and/or a specific role.
 *
 * Props:
 *  isAuthenticated    - bool
 *  userRole           - string (normalized role key from roles.js)
 *  requiredRole       - string (minimum role required, e.g. 'admin')
 *  requiredPermission - string (PERMISSIONS key, e.g. 'VIEW_ALL_USERS')
 *  fallback           - node  (custom unauthorized UI)
 *  onLoginRequest     - fn   (called when unauthenticated user clicks Login)
 *  children           - node
 */
const ProtectedRoute = ({
  isAuthenticated,
  userRole,
  requiredRole,
  requiredPermission,
  fallback,
  onLoginRequest,
  children,
}) => {
  if (!isAuthenticated) {
    if (fallback) return fallback;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please log in to access this section.</p>
          {onLoginRequest && (
            <button
              onClick={onLoginRequest}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
            >
              Login
            </button>
          )}
        </div>
      </div>
    );
  }

  if (requiredRole) {
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    if (userLevel < requiredLevel) {
      if (fallback) return fallback;
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-2">
              This section requires{' '}
              <span className="font-semibold text-red-600">{getRoleDisplayName(requiredRole)}</span>{' '}
              access or higher.
            </p>
            <p className="text-sm text-gray-500">
              Your current role:{' '}
              <span className="font-medium">{getRoleDisplayName(userRole)}</span>
            </p>
          </div>
        </div>
      );
    }
  }

  if (requiredPermission && !hasPermission(userRole, requiredPermission)) {
    if (fallback) return fallback;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Insufficient Permissions</h2>
          <p className="text-gray-600">You do not have permission to access this area.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
